import { courseGuideLabel } from '../../../packages/domain/src/26-courses.mjs';
import { observeContinuation } from '../ui/26-scroll-continuation.mjs';
/** Course walls preserve ordinal reading order and fixed controls. The server owns content,
 * review state, authorization and private read markers. Drafts are clearly separate previews. */
export function createCourses(ctx) {
  let generation = 0,
    continuation;
  function disposeCourse() {
    generation++;
    continuation?.disconnect();
    continuation = undefined;
  }
  function renderCourse(id) {
    disposeCourse();
    const current = ++generation,
      actor = ctx.state?.me.id;
    const card = ctx.e('section', 'course-wall settings-card');
    const status = ctx.e('p', 'subtle', 'Loading the ordered lesson wall…');
    status.setAttribute('role', 'status');
    const controls = ctx.e('div', 'button-row course-modules'),
      list = ctx.e('div', 'course-lessons');
    const more = ctx.button('Retry loading lessons', () => load(false), 'secondary-button');
    const boundary = ctx.e('p', 'subtle');
    boundary.setAttribute('role', 'status');
    more.hidden = true;
    card.append(
      ctx.link('← Learning', '#/learning', 'text-button'),
      status,
      controls,
      list,
      boundary,
      more,
    );
    ctx.view.append(card);
    let moduleId = '',
      cursor = null,
      busy = false,
      preview = false,
      course,
      requestGeneration = 0,
      controlVersion = null,
      coverageNote;
    const rendered = new Set();
    const active = () =>
      current === generation &&
      card.isConnected &&
      ctx.state?.me.id === actor &&
      ctx.route()[0] === 'course' &&
      ctx.route()[1] === id;
    function lessonCard(lesson) {
      const article = ctx.e('article', 'settings-card course-lesson');
      article.id = `lesson-${lesson.id}`;
      article.append(
        ctx.e(
          'p',
          'eyebrow',
          [
            course.modules.find((module) => module.id === lesson.moduleId)?.title,
            lesson.readingRef ?? `Reading ${lesson.ordinal}`,
          ]
            .filter(Boolean)
            .join(' · '),
        ),
        ctx.e('h2', '', lesson.title),
      );
      const scope = [];
      if (lesson.scope.tier === 'higher_only') scope.push('Higher tier');
      if (lesson.scope.tier === 'foundation_only') scope.push('Foundation tier only');
      if (lesson.scope.tier === 'both_with_higher_extension')
        scope.push('Includes Higher-tier extensions');
      if (lesson.scope.asScope === 'a_level_only') scope.push('A Level only');
      if (lesson.scope.biologyOnly === 'whole_leaf') scope.push('Separate Biology specification');
      if (lesson.scope.separateScienceOnly === 'whole_leaf')
        scope.push(`Separate ${course.subject} specification`);
      if (lesson.scope.separateScienceOnly === 'partial_extension')
        scope.push(`Includes separate ${course.subject} extensions`);
      if (scope.length) article.append(ctx.e('p', 'subtle', scope.join(' · ')));
      if (lesson.preview) {
        article.append(
          ctx.e('p', 'form-error', 'Draft preview: not published or independently reviewed.'),
        );
        for (const paragraph of lesson.content.paragraphs)
          article.append(ctx.e('p', '', paragraph));
        const example = ctx.e('section', 'form-stack');
        example.append(
          ctx.e('h3', '', 'Worked example'),
          ctx.e('p', '', lesson.content.workedExample.prompt),
        );
        for (const step of lesson.content.workedExample.steps) example.append(ctx.e('p', '', step));
        example.append(ctx.e('p', '', lesson.content.workedExample.answer));
        article.append(example);
      } else article.append(ctx.postCard(lesson.post));
      if (lesson.content) {
        const check = ctx.e('details', 'feature-disclosure');
        check.append(
          ctx.e('summary', '', 'Check your understanding'),
          ctx.e('p', '', lesson.content.check.question),
        );
        const answer = ctx.e('details');
        answer.append(
          ctx.e('summary', '', 'Show the explanation'),
          ctx.e('p', '', lesson.content.check.answer),
          ctx.e('p', '', lesson.content.check.rationale),
        );
        check.append(answer);
        article.append(check);
      } else if (lesson.contentCorrected) {
        article.append(
          ctx.e(
            'p',
            'subtle',
            'The publisher revised this lesson. Practice for the earlier version is hidden until it is reviewed again.',
          ),
        );
      }
      if (lesson.prerequisites?.length) {
        const earlier = ctx.e('p', 'subtle', 'Suggested earlier reading: ');
        for (const [index, prerequisite] of lesson.prerequisites.entries()) {
          if (index) earlier.append(ctx.e('span', '', ' · '));
          earlier.append(ctx.link(prerequisite.title, `#/post/${prerequisite.postId}`));
        }
        earlier.append(ctx.e('span', '', '. You can explore in any order.'));
        article.append(earlier);
      }
      if (lesson.canMarkRead) {
        const progress = ctx.e(
          'p',
          'subtle',
          lesson.readAt
            ? 'Marked read by you. This is not an assessment result.'
            : 'Private reading marker; not a test or certificate.',
        );
        const action = ctx.button(
          lesson.readAt ? 'Mark unread' : 'Mark read',
          async () => {
            if (!active() || action.disabled) return;
            action.disabled = true;
            try {
              const value = await ctx.api(`/api/courses/${encodeURIComponent(id)}/progress`, {
                lessonId: lesson.id,
                lessonRevision: lesson.revision,
                read: !lesson.readAt,
                requestId: crypto.randomUUID(),
              });
              if (!active()) return;
              lesson.readAt = value.readAt;
              action.textContent = value.readAt ? 'Mark unread' : 'Mark read';
              progress.textContent = value.readAt
                ? 'Marked read by you. This is not an assessment result.'
                : 'Private reading marker; not a test or certificate.';
            } catch (error) {
              if (active()) ctx.toast(error.message);
            } finally {
              if (active()) action.disabled = false;
            }
          },
          'secondary-button',
        );
        article.append(ctx.append(ctx.e('div', 'button-row'), action), progress);
      }
      return article;
    }
    function renderControls() {
      controlVersion = course.version;
      controls.replaceChildren();
      const choices = new Map();
      controls.append(
        ctx.link('Publisher page', `#/profile/${course.publisherId}`, 'secondary-button'),
      );
      const choose = (title, value) => {
        const button = ctx.button(
          title,
          () => {
            if (moduleId === value) return;
            moduleId = value;
            for (const [choice, selected] of choices)
              choice.setAttribute('aria-pressed', String(selected === value));
            load(true);
          },
          'chip',
        );
        choices.set(button, value);
        button.setAttribute('aria-pressed', String(moduleId === value));
        return button;
      };
      controls.append(choose('All modules', ''));
      for (const module of course.modules) controls.append(choose(module.title, module.id));
      if (ctx.state.isSuperAdmin === true) {
        const toggle = ctx.button(
          preview ? 'Hide pending drafts' : 'Preview pending drafts',
          () => {
            preview = !preview;
            toggle.textContent = preview ? 'Hide pending drafts' : 'Preview pending drafts';
            toggle.setAttribute('aria-pressed', String(preview));
            load(true);
          },
          'secondary-button',
        );
        toggle.setAttribute('aria-pressed', String(preview));
        controls.append(toggle);
      }
    }
    async function load(reset) {
      if (!active() || (busy && !reset)) return;
      const request = ++requestGeneration;
      busy = true;
      continuation?.disconnect();
      continuation = undefined;
      more.hidden = true;
      boundary.textContent = 'Loading lessons…';
      more.disabled = true;
      status.textContent = 'Loading lessons…';
      if (reset) {
        cursor = null;
        rendered.clear();
        list.replaceChildren();
        more.hidden = true;
      }
      const parameters = new URLSearchParams({ limit: '12', preview: String(preview) });
      if (moduleId) parameters.set('moduleId', moduleId);
      if (cursor) parameters.set('cursor', cursor);
      try {
        const page = await ctx.api(`/api/courses/${encodeURIComponent(id)}/lessons?${parameters}`);
        if (!active() || request !== requestGeneration) return;
        course = page.course;
        const history = ctx.platform?.history ?? globalThis.history;
        if (typeof course.id === 'string' && course.id !== id && history?.replaceState) {
          // Replace legacy bookmarks without remounting the wall or losing its cursor.
          history.replaceState(null, '', `#/course/${encodeURIComponent(course.id)}`);
          id = course.id;
        }
        if (!card.querySelector('h1')) {
          const title = ctx.e('h1', '', course.title);
          card.prepend(title);
          card.insertBefore(ctx.e('p', 'subtle', courseGuideLabel(course)), status);
          const guide = ctx.e('details', 'feature-disclosure course-guide');
          coverageNote = ctx.e('p', 'subtle');
          guide.append(
            ctx.e('summary', '', 'About this guide and its coverage'),
            ctx.e('p', 'subtle', course.scopeNote),
            coverageNote,
          );
          card.insertBefore(guide, controls);
        }
        const topics = course.coverage.categories.topic;
        status.textContent = `${course.coverage.publishedAvailableLessons} published lessons · ${topics.withPublishedLesson}/${topics.required} topic ${course.qualification === 'enrichment' ? 'objectives' : 'requirements'} have published lessons.${page.preview ? ' Owner draft preview.' : ''}`;
        coverageNote.textContent =
          course.coverage.statement ??
          'Full practical, skill and specification coverage is not verified.';
        if (controlVersion !== course.version) renderControls();
        for (const lesson of page.lessons)
          if (!rendered.has(lesson.id)) {
            rendered.add(lesson.id);
            list.append(lessonCard(lesson));
          }
        cursor = page.cursor;
        more.hidden = true;
        boundary.textContent = page.hasMore ? 'More lessons appear as you scroll.' : '';
        if (page.hasMore) {
          continuation = observeContinuation(boundary, () => load(false), {
            platform: ctx.platform ?? globalThis,
            active,
            margin: 700,
          });
        }
        if (!rendered.size)
          list.append(
            ctx.empty(
              page.preview
                ? 'No authored drafts in this module yet.'
                : 'No reviewed lessons in this module yet.',
            ),
          );
      } catch (error) {
        if (active() && request === requestGeneration) {
          boundary.textContent = error.message;
          more.hidden = false;
          more.textContent = 'Retry loading lessons';
        }
      } finally {
        if (active() && request === requestGeneration) {
          busy = false;
          more.disabled = false;
        }
      }
    }
    load(true);
  }
  return { renderCourse, disposeCourse };
}
